import { useQuery } from '@apollo/client';
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuthContext } from '@myphx-engineering/auth/client';

import {
    type ConsentDocument,
    GetPendingConsentsDocument,
} from '../../graphql/generated/graphql';
import { getAbsoluteRoute } from '../../util/get-absolute-route';
import { groupConsentsConsecutively } from '../../util/group-consents-consecutively';
import { Loader } from '../common/Loader';

import { CaptureConsents } from './CaptureConsents';

type ConsentWrapperProps = {
    children: React.ReactNode;
};

export const ConsentWrapper = ({ children }: ConsentWrapperProps) => {
    const { isAuthenticated } = useAuthContext();
    const { pathname } = useLocation();
    const excludeConsentRoutes = ['/logout', '/login/callback'];
    const excludeConsents = excludeConsentRoutes.includes(pathname);

    const { data, loading, error } = useQuery(GetPendingConsentsDocument, {
        errorPolicy: 'all',
        skip: !isAuthenticated,
    });

    if (isAuthenticated && loading) {
        return <Loader />;
    }

    if (error) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    const pendingConsents = (data?.app?.pendingConsents ??
        []) as ConsentDocument[];

    // TODO: Delete after real data is populated in PRO. For testing
    // if (pendingConsents.length) {
    //     pendingConsents = [
    //         ...pendingConsents,
    //         {
    //             id: '4',
    //             consentFlowId: '1',
    //             hash: 'b66215f3cb2202f0f6d9763ff0fc188bf509edc40f787fc20fc8163ee4fd5fe2',
    //             currentVersionId: '3ef25189-0c62-4816-b2ce-b6ab69c6a037',
    //             required: false,
    //             sortOrder: 2,
    //             htmlContent: '<p><span></span></p>',
    //             consentType: 'marketing',
    //             title: 'Marketing research',
    //             majorVersion: 1,
    //             minorVersion: 0,
    //             publishedConsentBox: {
    //                 htmlContent:
    //                     '<p><span>I agree to be contacted about opportunities to participate in market research.</span></p>',
    //                 __typename: 'PublishedConsentBox',
    //                 createdAt: '',
    //                 id: '',
    //                 publishedConsentDocumentId: '',
    //                 required: false,
    //                 sortOrder: 0,
    //                 updatedAt: '',
    //             },
    //             __typename: 'ConsentDocument',
    //         },
    //         {
    //             id: '6',
    //             consentFlowId: '1',
    //             hash: '814ea190c726c6699cf8652ee93c15a7125f8dfdc1c8860dde2c01bd5aaee041',
    //             currentVersionId: 'b421dac3-bd9d-4a51-ba68-a5ca45b5c3f2',
    //             required: false,
    //             sortOrder: 3,
    //             htmlContent: '<p><span></span></p>',
    //             consentType: 'marketing',
    //             title: 'Medical research',
    //             majorVersion: 1,
    //             minorVersion: 0,
    //             publishedConsentBox: {
    //                 htmlContent:
    //                     '<p><span>I agree to be contacted about opportunities to participate in medical research.</span></p>',
    //                 __typename: 'PublishedConsentBox',
    //                 createdAt: '',
    //                 id: '',
    //                 publishedConsentDocumentId: '',
    //                 required: false,
    //                 sortOrder: 0,
    //                 updatedAt: '',
    //             },
    //             __typename: 'ConsentDocument',
    //         },
    //     ];
    // }

    if (
        !isAuthenticated ||
        !pendingConsents ||
        pendingConsents.length === 0 ||
        excludeConsents
    ) {
        return <>{children}</>;
    }

    const groupedConsents = groupConsentsConsecutively(pendingConsents);

    return <CaptureConsents groupedConsents={groupedConsents} />;
};
